/**
 * LoginScreen — minimal three-element layout:
 *   1. Fancall logo in the top-left corner
 *   2. Daily thought (rotating CEO-grade quote, fresh every page mount)
 *   3. Sign-in form
 *
 * Stripped of all marketing copy, hero headlines, capability lists, ambient
 * orbs, live clocks, and rotators per CEO direction (2026-05-04). The login
 * page is now a tight focus surface — read the thought, sign in, get to work.
 */

import React, { useState, useMemo, useEffect } from "react";
import { authApi } from "../../api";
import { tokensV2 as T } from "../../preview/v2/tokens.v2";

function useIsNarrow() {
  const [narrow, setNarrow] = useState(() =>
    typeof window !== "undefined" ? window.innerWidth < 760 : false
  );
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onResize = () => setNarrow(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  return narrow;
}

const QUOTES = [
  { text: "The most important thing is to make every decision as if your company depended on it. Because it does.", author: "Brian Chesky · Airbnb" },
  { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
  { text: "If you double the number of experiments you do per year, you're going to double your inventiveness.", author: "Jeff Bezos · Amazon" },
  { text: "Your most unhappy customers are your greatest source of learning.", author: "Bill Gates · Microsoft" },
  { text: "Success is a lousy teacher. It seduces smart people into thinking they can't lose.", author: "Bill Gates · Microsoft" },
  { text: "The riskiest thing we can do is just maintain the status quo.", author: "Bob Iger · Disney" },
  { text: "Move fast with stable infrastructure.", author: "Mark Zuckerberg · Meta" },
  { text: "Stay focused, go fast, keep doing the things that work.", author: "Sundar Pichai · Google" },
  { text: "Hard things are hard. There are no easy answers or magic formulas.", author: "Ben Horowitz · a16z" },
  { text: "Vision without execution is hallucination.", author: "Thomas Edison" },
  { text: "It's not about ideas. It's about making ideas happen.", author: "Scott Belsky · Behance" },
  { text: "If you're not embarrassed by the first version of your product, you've launched too late.", author: "Reid Hoffman · LinkedIn" },
  { text: "The best founders set the bar for everyone around them.", author: "Sam Altman · OpenAI" },
  { text: "Customers don't care about your solution. They care about their problems.", author: "Dave McClure · 500 Startups" },
  { text: "What gets measured gets managed.", author: "Peter Drucker" },
  { text: "Done is better than perfect.", author: "Sheryl Sandberg · Meta" },
  { text: "If you do not know how to ask the right question, you discover nothing.", author: "W. Edwards Deming" },
  { text: "Make something people want.", author: "Paul Graham · Y Combinator" },
  { text: "The only way to do great work is to love what you do.", author: "Steve Jobs · Apple" },
  { text: "Discipline equals freedom.", author: "Jocko Willink" },
  { text: "Founders work harder. So do their dashboards.", author: "Fancall" },
  { text: "Numbers don't lie. But the wrong number, beautifully presented, will lie all day.", author: "Fancall" },
  { text: "Build for the user you want, not the user you have.", author: "Naval Ravikant · AngelList" },
  { text: "The best way to predict the future is to create it.", author: "Peter Drucker" },
  { text: "Great companies are built by founders who refuse to lose.", author: "Marc Andreessen · a16z" },
];

function pickQuote() {
  return QUOTES[Math.floor(Math.random() * QUOTES.length)];
}

/**
 * CornerLogo — Fancall lockup in the page's top-left corner. Sized to read
 * confidently at desktop scale (~110px tall) and shrink gracefully on narrow
 * viewports. Falls back to an orange-gradient F + "fancall" wordmark if the
 * PNG isn't deployed.
 */
function CornerLogo({ narrow }) {
  const [imgFailed, setImgFailed] = useState(false);

  if (imgFailed) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{
          width: narrow ? 44 : 56, height: narrow ? 44 : 56, borderRadius: 14,
          background: `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#FFFFFF", fontSize: narrow ? 22 : 28, fontWeight: 800,
        }}>F</div>
        <div style={{
          fontSize: narrow ? 22 : 28,
          fontWeight: 800, letterSpacing: "-0.02em", color: T.text.primary,
        }}>
          Fancall
        </div>
      </div>
    );
  }

  return (
    <img
      src="/fancall-logo.png"
      alt="Fancall"
      onError={() => setImgFailed(true)}
      style={{
        display: "block",
        height: narrow ? 78 : 110,
        width: "auto",
      }}
    />
  );
}

export function LoginScreen({ onLogin }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  // If the app redirected here because the session timed out, show a one-time
  // notice (the flag is set by the global 401 interceptor) so the user knows why.
  const [sessionExpired] = useState(() => {
    try {
      if (sessionStorage.getItem("fc_session_expired")) {
        sessionStorage.removeItem("fc_session_expired");
        return true;
      }
    } catch (_) { /* private mode — no flag */ }
    return false;
  });
  // Pick once per mount — refresh = new quote, but stable while typing.
  const quote = useMemo(() => pickQuote(), []);
  const narrow = useIsNarrow();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) { setError("Enter username and password"); return; }
    setLoading(true);
    setError("");
    try {
      const data = await authApi.login(username.trim().toLowerCase(), password);
      onLogin(data.token, data.user);
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: "100%", padding: "12px 14px", borderRadius: 10,
    border: `1px solid ${T.border.default}`,
    fontSize: 14, fontFamily: "inherit", color: T.text.primary,
    background: T.surface.card,
    outline: "none", boxSizing: "border-box",
    transition: "border-color 160ms ease, box-shadow 160ms ease",
  };
  const onInputFocus = (e) => {
    e.target.style.borderColor = T.brand.base;
    e.target.style.boxShadow = `0 0 0 3px ${T.brand.soft}`;
  };
  const onInputBlur = (e) => {
    e.target.style.borderColor = T.border.default;
    e.target.style.boxShadow = "none";
  };

  return (
    <div style={{
      minHeight: "100vh", minHeight: "100dvh",
      backgroundColor: T.surface.canvas,
      color: T.text.primary,
      fontFamily: T.font.sans,
      position: "relative",
      display: "flex", alignItems: "center", justifyContent: "center",
      padding: narrow ? "120px 20px 32px" : "32px",
    }}>
      {/* Logo — fixed in the top-left corner of the viewport. */}
      <div style={{
        position: "absolute",
        top: narrow ? 24 : 40,
        left: narrow ? 24 : 48,
        zIndex: 2,
      }}>
        <CornerLogo narrow={narrow} />
      </div>

      {/* Centred body — daily thought card + sign-in card side by side on
          desktop, stacked on narrow viewports. */}
      <div style={{
        width: "100%", maxWidth: 980,
        display: "grid",
        gridTemplateColumns: narrow ? "1fr" : "minmax(280px, 1fr) minmax(340px, 420px)",
        gap: narrow ? 24 : 40,
        alignItems: "center",
      }}>
        {/* Daily thought */}
        <section>
          <div style={{
            display: "flex", alignItems: "center", gap: 8, marginBottom: 12,
          }}>
            <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: T.brand.base,
              boxShadow: `0 0 8px ${T.brand.base}`,
            }} />
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: "0.10em",
              textTransform: "uppercase", color: T.text.muted,
            }}>
              Thought of the day
            </span>
          </div>
          <blockquote style={{
            margin: 0, padding: narrow ? "20px 22px" : "26px 28px",
            background: T.surface.card,
            border: `1px solid ${T.border.subtle}`,
            borderLeft: `3px solid ${T.brand.base}`,
            borderRadius: 14,
            boxShadow: "0 6px 20px rgba(31,42,63,0.04)",
          }}>
            <div style={{
              fontSize: narrow ? 17 : 19,
              fontWeight: 600, lineHeight: 1.45,
              color: T.text.primary, letterSpacing: "-0.005em",
            }}>
              &ldquo;{quote.text}&rdquo;
            </div>
            <div style={{
              marginTop: 14, fontSize: 12, fontWeight: 700,
              color: T.text.secondary, letterSpacing: "0.02em",
            }}>
              — {quote.author}
            </div>
          </blockquote>
        </section>

        {/* Sign-in card */}
        <section style={{
          background: T.surface.card,
          borderRadius: 18,
          border: `1px solid ${T.border.subtle}`,
          boxShadow: "0 16px 48px rgba(31,42,63,0.08)",
          padding: 32,
        }}>
          <h1 style={{
            margin: 0, fontSize: 24, fontWeight: 800,
            color: T.text.primary, letterSpacing: "-0.02em",
          }}>
            Sign in
          </h1>
          <p style={{ margin: "6px 0 24px", fontSize: 13, color: T.text.secondary, lineHeight: 1.5 }}>
            Use your dashboard credentials.
          </p>

          {sessionExpired && (
            <div role="status" style={{
              padding: "10px 14px", borderRadius: 10, marginBottom: 16,
              background: T.brand.soft,
              border: `1px solid ${T.brand.base}33`,
              fontSize: 12.5, color: T.brand.deep, fontWeight: 600,
            }}>
              Your session timed out. Please sign in again.
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={{ marginBottom: 16 }}>
              <label htmlFor="fc-login-username" style={{
                display: "block", fontSize: 11, fontWeight: 700,
                color: T.text.secondary, marginBottom: 6,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>Username</label>
              <input
                id="fc-login-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. ceo, cmo, superadmin"
                autoComplete="username"
                autoFocus
                style={inputStyle}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label htmlFor="fc-login-password" style={{
                display: "block", fontSize: 11, fontWeight: 700,
                color: T.text.secondary, marginBottom: 6,
                textTransform: "uppercase", letterSpacing: "0.06em",
              }}>Password</label>
              <input
                id="fc-login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                autoComplete="current-password"
                style={inputStyle}
                onFocus={onInputFocus}
                onBlur={onInputBlur}
              />
            </div>

            {error && (
              <div role="alert" style={{
                padding: "10px 14px", borderRadius: 10, marginBottom: 16,
                background: T.status.badBg,
                border: `1px solid ${T.status.bad}33`,
                fontSize: 12, color: T.status.bad, fontWeight: 600,
              }}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: "100%", padding: "14px",
                borderRadius: 12, border: "none",
                cursor: loading ? "wait" : "pointer",
                background: loading
                  ? T.brand.deepSoft
                  : `linear-gradient(135deg, ${T.brand.base} 0%, ${T.brand.deep} 100%)`,
                color: "#FFFFFF", fontSize: 14, fontWeight: 700,
                letterSpacing: "0.01em",
                boxShadow: loading ? "none" : `0 8px 24px ${T.brand.soft}`,
                opacity: loading ? 0.85 : 1,
                transition: "transform 120ms ease, box-shadow 160ms ease, opacity 120ms ease",
              }}
              onMouseEnter={(e) => { if (!loading) e.currentTarget.style.transform = "translateY(-1px)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}
            >
              {loading ? "Signing in…" : "Sign in"}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
}
